import type { AiState } from './ai';
import type { CarPerf, CarState } from './car';
import type { CouchMatch, CouchPhase } from './couch';
import type { LapState } from './laps';
import type { RaceWorld, RacerStatus } from './race';

/**
 * Save and restore the mutable state of a race (design.md B8.2, B12.2):
 * rollback netcode rewinds to an earlier tick and re-simulates when a
 * remote player's real inputs differ from the guess. Plain copies only.
 */

interface RacerSnap {
  car: CarState;
  perf: CarPerf;
  lap: LapState;
  ai: AiState;
  status: RacerStatus;
  finishTick: number;
  outProgress: number;
  crashes: number;
  ghostTicks: number;
  lowSpeedTicks: number;
  inTow: boolean;
}

export interface RaceSnap {
  tick: number;
  leaderFinishedTick: number;
  finishOrder: number[];
  over: boolean;
  racers: RacerSnap[];
}

export function saveRace(w: RaceWorld): RaceSnap {
  return {
    tick: w.tick,
    leaderFinishedTick: w.leaderFinishedTick,
    finishOrder: w.finishOrder.slice(),
    over: w.over,
    racers: w.racers.map((r) => ({
      car: { ...r.car },
      perf: { ...r.perf },
      lap: { ...r.lap },
      ai: { ...r.ai, rng: { ...r.ai.rng } },
      status: r.status,
      finishTick: r.finishTick,
      outProgress: r.outProgress,
      crashes: r.crashes,
      ghostTicks: r.ghostTicks,
      lowSpeedTicks: r.lowSpeedTicks,
      inTow: r.inTow,
    })),
  };
}

export function loadRace(w: RaceWorld, s: RaceSnap): void {
  w.tick = s.tick;
  w.leaderFinishedTick = s.leaderFinishedTick;
  w.finishOrder = s.finishOrder.slice();
  w.over = s.over;
  w.events = [];
  s.racers.forEach((rs, i) => {
    const r = w.racers[i];
    r.car = { ...rs.car };
    r.perf = { ...rs.perf };
    r.lap = { ...rs.lap };
    r.ai = { ...rs.ai, rng: { ...rs.ai.rng } };
    r.status = rs.status;
    r.finishTick = rs.finishTick;
    r.outProgress = rs.outProgress;
    r.crashes = rs.crashes;
    r.ghostTicks = rs.ghostTicks;
    r.lowSpeedTicks = rs.lowSpeedTicks;
    r.inTow = rs.inTow;
  });
}

export interface CouchSnap {
  race: RaceSnap;
  lights: number[];
  roundWins: number[];
  drops: number[];
  inRound: boolean[];
  offTicks: number[];
  phase: CouchPhase;
  phaseTicks: number;
  round: number;
  roundTicks: number;
  boxScale: number;
  pending: number;
  winner: number;
  camX: number;
  camY: number;
  tick: number;
}

export function saveCouch(m: CouchMatch): CouchSnap {
  return {
    race: saveRace(m.race),
    lights: m.lights.slice(),
    roundWins: m.roundWins.slice(),
    drops: m.drops.slice(),
    inRound: m.inRound.slice(),
    offTicks: m.offTicks.slice(),
    phase: m.phase,
    phaseTicks: m.phaseTicks,
    round: m.round,
    roundTicks: m.roundTicks,
    boxScale: m.boxScale,
    pending: m.pending,
    winner: m.winner,
    camX: m.camX,
    camY: m.camY,
    tick: m.tick,
  };
}

export function loadCouch(m: CouchMatch, s: CouchSnap): void {
  loadRace(m.race, s.race);
  const copy = <T>(dst: T[], src: T[]) => src.forEach((v, i) => (dst[i] = v));
  copy(m.lights, s.lights);
  copy(m.roundWins, s.roundWins);
  copy(m.drops, s.drops);
  copy(m.inRound, s.inRound);
  copy(m.offTicks, s.offTicks);
  m.phase = s.phase;
  m.phaseTicks = s.phaseTicks;
  m.round = s.round;
  m.roundTicks = s.roundTicks;
  m.boxScale = s.boxScale;
  m.pending = s.pending;
  m.winner = s.winner;
  m.camX = s.camX;
  m.camY = s.camY;
  m.tick = s.tick;
  m.events = [];
}
